import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight, CheckCircle2, Sparkles, UserRound, Cpu, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fade, Eyebrow, usePageMeta } from "@/components/SiteLayout";
import { WorkflowStrip } from "@/components/WorkflowStrip";

const PROCESS = ["Understand", "Strategize", "Stress Test", "Prepare", "Validate", "Deliver"];
const LIFECYCLE = ["Clinical Development", "Evidence Generation", "Comparator Strategy", "RWE", "Indirect Comparison", "JCA", "National HTA", "Pricing & Access", "Lifecycle Updates"];
const FIRST_JCA_LESSONS = [
  { title: "Most PICOs go unanswered", desc: "Across published assessments, evidence packages answer only a fraction of the PICOs posed. Anticipating answerable PICOs is now the core JCA discipline." },
  { title: "Comparator evidence determines success", desc: "The comparisons that survive scrutiny are the ones with well-documented comparator evidence. Poorly sourced comparisons are excluded outright." },
  { title: "Evidence gaps — not efficacy — drive uncertainty", desc: "Clinical signals tend to hold up. What generates \u201cinterpret with caution\u201d verdicts is missing data, small effective samples and unadjustable variables." },
  { title: "Assessors rebuild analyses independently", desc: "Missing-but-feasible analyses get computed by the assessors themselves — on their terms, not the sponsor's. Anything you leave open, they will fill." },
];

export default function Approach() {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);
  usePageMeta(
    "Our Approach — AI at Scale, Expert Judgment | JCA Matrix™",
    "A six-stage engagement model powered by JCA Matrix™ and interpreted by senior MarksMan HEOR consultants — governing evidence strategy across the entire lifecycle."
  );
  return (
    <>
      <section className="pt-36 pb-16 md:pt-44 md:pb-20 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Eyebrow>Our Approach</Eyebrow>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
            AI at scale. <span className="text-blue-600">Judgment where it matters.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            A structured engagement model powered by the JCA Matrix™ JCA Readiness & Strategy Platform and
            interpreted by senior MarksMan consultants — governing your evidence strategy across the entire lifecycle.
          </p>
        </div>
      </section>

      {/* Six stages */}
      <section className="py-20" data-testid="approach-stages">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Eyebrow>How we work</Eyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl">Six stages. One executive verdict.</h2>
          <p className="mt-5 max-w-2xl text-slate-600 leading-relaxed">
            Each stage is powered by JCA Matrix™ and interpreted by MarksMan consultants. The full methodology is shared during scoping, under confidentiality.
          </p>
          <div className="mt-12 flex flex-wrap lg:flex-nowrap items-center gap-2" data-testid="how-we-work-grid">
            {PROCESS.map((p, i) => (
              <React.Fragment key={p}>
                <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                  onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)} data-testid={`stage-card-${i}`}
                  className={`flex items-center gap-2 rounded-xl border bg-white px-3 py-3 lg:flex-1 lg:justify-center cursor-default transition-all duration-300 ${hovered === i ? "-translate-y-1 shadow-lg shadow-blue-600/10 border-blue-300 bg-blue-50/40" : "border-slate-200"}`}>
                  <span className="h-6 w-6 rounded-full text-white flex items-center justify-center text-[11px] font-semibold shrink-0 bg-blue-600">{i + 1}</span>
                  <span className={`font-display font-semibold tracking-tight text-sm whitespace-nowrap transition-colors duration-300 ${hovered === i ? "text-blue-700" : "text-slate-900"}`}>{p}</span>
                </motion.div>
                {i < PROCESS.length - 1 && <ChevronRight className={`h-4 w-4 hidden lg:block shrink-0 transition-all duration-300 ${hovered === i ? "text-blue-600 translate-x-1 scale-125" : "text-slate-300"}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Human + Machine */}
      <section className="py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <Eyebrow>Human + Machine</Eyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Our AI doesn't replace experts. <span className="text-blue-600">It augments them.</span></h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6 items-stretch" data-testid="augmentation-section">
            <div className="rounded-2xl border border-slate-200 bg-white p-7 text-left hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/5 transition-all duration-300">
              <Cpu className="h-7 w-7 text-blue-600" strokeWidth={1.5} />
              <h3 className="mt-4 font-display text-lg font-semibold">JCA Matrix™ AI</h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                {["Extracts evidence at scale", "Compares against precedent", "Forecasts PICOs & outcomes", "Identifies gaps & risks"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-blue-500 shrink-0" /> {t}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-7 text-left hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/5 transition-all duration-300">
              <UserRound className="h-7 w-7 text-blue-600" strokeWidth={1.5} />
              <h3 className="mt-4 font-display text-lg font-semibold">MarksMan Experts</h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                {["Validate the findings that matter", "Interpret against assessor behaviour", "Recommend the strategy", "Defend it at your board"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> {t}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl bg-slate-900 p-7 text-left flex flex-col hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-900/20 transition-all duration-300">
              <FileText className="h-7 w-7 text-blue-400" strokeWidth={1.5} />
              <h3 className="mt-4 font-display text-lg font-semibold text-white">Executive Decision Package</h3>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed flex-1">
                One synthesized verdict, traceable to every analysis, delivered with the evidence behind it — and defended in your governance meeting by the consultants who built it.
              </p>
            </div>
          </div>
          <WorkflowStrip className="mt-14" />
        </div>
      </section>

      {/* Evidence lifecycle */}
      <section className="py-20" data-testid="evidence-lifecycle">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Eyebrow>The evidence lifecycle</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">One partner across the <span className="text-blue-600">entire evidence lifecycle</span></h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Most consultants engage at a single point. MarksMan governs your evidence strategy end to end — so decisions made in Phase II still hold up in national pricing negotiations years later.
            </p>
          </div>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-y-3 gap-x-1" data-testid="lifecycle-flow">
            {LIFECYCLE.map((s, i) => (
              <React.Fragment key={s}>
                <motion.span variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} data-testid={`lifecycle-node-${i}`}
                  className={`rounded-full border px-4 py-2 text-sm font-medium whitespace-nowrap transition-all duration-300 hover:-translate-y-0.5 ${s === "JCA" ? "bg-slate-900 border-slate-900 text-white shadow-lg shadow-slate-900/20" : "bg-white border-slate-200 text-slate-700 hover:border-blue-300 hover:text-blue-700"}`}>{s}</motion.span>
                {i < LIFECYCLE.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* First JCA lessons */}
      <section className="py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Eyebrow>Lessons from the published JCAs</Eyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight max-w-3xl">Every published assessment, dissected. The patterns are already clear.</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="first-jca-lessons">
            {FIRST_JCA_LESSONS.map((l, i) => (
              <motion.div key={l.title} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="rounded-2xl border border-slate-200 bg-white p-7 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/5 hover:border-slate-300 transition-all duration-300 flex flex-col">
                <div className="h-9 w-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center font-display font-semibold text-blue-600">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="mt-4 font-display text-lg font-semibold tracking-tight leading-snug">{l.title}</h3>
                <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{l.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button data-testid="approach-cta" onClick={() => navigate("/contact")} className="group bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-12 px-6 text-base font-medium">
              Request a JCA Evidence Stress Test <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
